/**
 * CalendarPageGridWithPerf Component
 * 
 * Purpose:
 * Calendar grid component wrapper that adds performance tracking to the BookingCalendar component.
 * Tracks first render time, measures time to grid render, and logs performance summary when
 * PERF_DEBUG is enabled. Wraps BookingCalendar with identical prop interface.
 * 
 * High-level Workflow:
 * 1. Receives all props needed for BookingCalendar (bookings, channels, month, handlers, etc.).
 * 2. On first render, records grid render time using performance.mark and performance.measure.
 * 3. Measures time from mount (mountTimeRef) to grid first render.
 * 4. Logs performance summary (time to grid render, bookings fetch time, properties fetch time)
 *   when PERF_DEBUG is enabled.
 * 5. Renders BookingCalendar with all props passed through unchanged.
 * 
 * Assumptions & Invariants:
 * - Performance tracking: Only tracks first render (gridRenderTimeRef.current is null initially,
 *   set to performance.now() on first render, never reset). Subsequent renders do not trigger
 *   new performance marks.
 * - Mount time reference: mountTimeRef.current must be set before this component renders
 *   (set in CalendarPage mount effect). If mountTimeRef.current is null, performance measures
 *   are skipped (graceful degradation).
 * - Performance debug flag: PERF_DEBUG is defined at module level (from env or URL param).
 *   Logging only occurs when PERF_DEBUG is true.
 * - Prop passthrough: All props are passed directly to BookingCalendar unchanged. This component
 *   is a pure wrapper that adds performance tracking without modifying behavior.
 * - Availability prop: availability prop defaults to empty object {} if not provided (for
 *   BookingCalendar compatibility).
 * 
 * Dependencies:
 * - Internal:
 *   - components/owner/BookingCalendar.jsx: Main calendar grid component
 * - External:
 *   - react: useRef, useEffect hooks
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 4 refactoring)
 * - 2026-01-26: Restored selectedDate prop passthrough to BookingCalendar (preserve visual selection highlight)
 */

import { useRef, useEffect } from 'react';
import BookingCalendar from './BookingCalendar.jsx';

// Performance debug flag (from env or URL param)
const PERF_DEBUG = typeof window !== 'undefined' && (
  import.meta.env.VITE_OWNER_PERF_DEBUG === '1' ||
  new URLSearchParams(window.location.search).get('perf_debug') === '1'
);

/**
 * Calendar grid component with performance tracking
 * 
 * @param {Array} bookings - Array of booking events for calendar display
 * @param {Object} channels - Channel mapping object (channel_code -> channel data)
 * @param {Date} month - Current month being displayed
 * @param {Date} monthStart - Start date of month (for date range filtering)
 * @param {Date} monthEnd - End date of month (for date range filtering)
 * @param {Function} onBookingClick - Handler called when booking is clicked
 * @param {Function} onDateClick - Handler called when date is clicked
 * @param {Function} onMonthChange - Handler called when month changes
 * @param {boolean} isCollapsed - Whether calendar is collapsed
 * @param {Function} onToggleCollapse - Handler called when collapse toggle is clicked
 * @param {Function} onOwnerBookingClick - Handler called when owner booking button is clicked
 * @param {boolean} isMobile - Whether mobile layout is active
 * @param {Date|null} selectedDate - Currently selected date (for visual highlighting in calendar grid)
 * @param {Object} mountTimeRef - Ref containing mount time (for performance tracking)
 * @param {number} bookingsFetchCount - Number of bookings fetch operations (for performance tracking)
 * @param {Object} availability - Availability data object (defaults to {} if not provided)
 */
export default function CalendarPageGridWithPerf({ 
  bookings, 
  channels, 
  month, 
  monthStart, 
  monthEnd, 
  onBookingClick, 
  onDateClick, 
  onMonthChange, 
  isCollapsed, 
  onToggleCollapse,
  onOwnerBookingClick,
  isMobile,
  selectedDate,
  mountTimeRef,
  bookingsFetchCount,
  availability,
}) {
  const gridRenderTimeRef = useRef(null);
  
  useEffect(() => {
    if (typeof window !== 'undefined' && window.performance) {
      if (!gridRenderTimeRef.current) {
        gridRenderTimeRef.current = performance.now();
        performance.mark('calendar_grid_first_render');
        
        // Measure time to grid render
        if (mountTimeRef.current) {
          performance.measure('time_to_grid_render_ms', 'owner_calendar_mount', 'calendar_grid_first_render');
        }
        
        // Measure overall time to interactive
        if (mountTimeRef.current) {
          performance.measure('overall_time_to_interactive_ms', 'owner_calendar_mount', 'calendar_grid_first_render');
        }
        
        // Log performance summary
        if (PERF_DEBUG) {
          const measures = {
            timeToGridRender: performance.getEntriesByName('time_to_grid_render_ms')[0]?.duration,
            overallTimeToInteractive: performance.getEntriesByName('overall_time_to_interactive_ms')[0]?.duration,
            bookingsFetchMs: performance.getEntriesByName('bookings_fetch_ms')[0]?.duration,
            propertiesFetchMs: performance.getEntriesByName('properties_fetch_ms')[0]?.duration,
          };
          
          console.log('[OWNER_PERF] Performance Summary', {
            ...measures,
            bookingsFetchCount,
            timestamp: new Date().toISOString(),
          });
        }
      }
    }
  }, [mountTimeRef, bookingsFetchCount]);
  
  return (
    <BookingCalendar
      bookings={bookings}
      channels={channels}
      month={month}
      monthStart={monthStart}
      monthEnd={monthEnd}
      onBookingClick={onBookingClick}
      onDateClick={onDateClick}
      onMonthChange={onMonthChange}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
      onOwnerBookingClick={onOwnerBookingClick}
      isMobile={isMobile}
      selectedDate={selectedDate}
      availability={availability || {}}
    />
  );
}
